import type {
  OfferConversionMode,
  OfferEmbedProvider,
  OfferTemplate,
  OfferTemplateContent,
  OfferTemplateContentInput,
  StripePriceOptionInput,
} from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { getReadableTextColor } from '@/utils/colors';

export type OfferEmbedRender =
  | { kind: 'iframe'; provider: OfferEmbedProvider; src: string }
  | { kind: 'whop-checkout'; checkoutPlanId: string };

// L'iframe est sur le domaine du service : allow-same-origin ne lui donne aucun accès à notre page.
// Sans allow-top-navigation, elle ne peut pas rediriger le live.
export const OFFER_EMBED_IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox';

export const OFFER_EMBED_IFRAME_ALLOW = 'payment; clipboard-write';

// Mêmes hôtes que le back : pas de sous-domaine, www redirige vers kalleo.io.
const KALLEO_HOSTS = new Set(['kalleo.io', 'www.kalleo.io']);

// Chaque client Symetrial a son sous-domaine. Seule la page /live s'intègre.
const SYMETRIAL_DOMAIN = 'symetrial.com';
const SYMETRIAL_LIVE_PATH = '/live';

// Le chemin seul ne prouve rien : n'importe quel site peut servir /widget/booking/x. Une nouvelle
// marque blanche s'ajoute ici.
const GOHIGHLEVEL_BOOKING_HOSTS = new Set(['api.leadconnectorhq.com', 'api.omneon.io']);
const GOHIGHLEVEL_BOOKING_PATH = /^\/widget\/booking\/[^/]+$/;
const GOHIGHLEVEL_EMBED_SCRIPT_PATH = '/js/form_embed.js';
// Le code par défaut, seul cas où l'iframe et le script sont sur deux domaines différents.
const GOHIGHLEVEL_DEFAULT_BOOKING_HOST = 'api.leadconnectorhq.com';
const GOHIGHLEVEL_DEFAULT_EMBED_SCRIPT_HOST = 'link.msgsndr.com';
const EXPLICIT_URL_PORT = /^https:\/\/[^/?#]*:(\d+)(?=[/?#]|$)/i;

const PROVIDER_HOSTS: Record<
  Exclude<OfferEmbedProvider, 'STRIPE_PRICING_TABLE' | 'GOHIGHLEVEL'>,
  (host: string) => boolean
> = {
  CALENDLY: (host) => isHostOrSubdomain(host, 'calendly.com'),
  ICLOSED: (host) => isHostOrSubdomain(host, 'iclosed.io'),
  TYPEFORM: (host) => host === 'form.typeform.com' || host === 'form.typeform.eu',
  WHOP: (host) => isHostOrSubdomain(host, 'whop.com'),
  KALLEO: (host) => KALLEO_HOSTS.has(host.toLowerCase()),
  SYMETRIAL: (host) => isHostOrSubdomain(host, SYMETRIAL_DOMAIN),
};

const WHOP_CHECKOUT_PLAN_ID = /^plan_[A-Za-z0-9]+$/;

const SYMETRIAL_BOOKED_MESSAGE_TYPE = 'meridian:booked';
const ISO_UTC_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?Z$/;

type OfferConversionFields = Pick<
  OfferTemplateContent,
  | 'conversionMode'
  | 'buttonLink'
  | 'embedProvider'
  | 'embedUrl'
  | 'checkoutPlanId'
  | 'embedFallbackUrl'
>;

export function isEmbedOffer(
  content: Pick<OfferTemplateContent, 'conversionMode'> | null | undefined,
): boolean {
  return content?.conversionMode === 'EMBED';
}

export function isStripeCheckoutOffer(
  content: Pick<OfferTemplateContent, 'conversionMode'> | null | undefined,
): boolean {
  return content?.conversionMode === 'STRIPE_EMBEDDED_CHECKOUT';
}

// Code embed comme paiement Stripe s'affichent dans le panneau, à la place du chat.
export function opensInOfferPanel(
  content: Pick<OfferTemplateContent, 'conversionMode'> | null | undefined,
): boolean {
  return isEmbedOffer(content) || isStripeCheckoutOffer(content);
}

export const OFFER_EMBED_CODE_MAX_LENGTH = 10000;

type OfferConversionInput = Pick<
  OfferTemplateContentInput,
  | 'conversionMode'
  | 'buttonLink'
  | 'embedCode'
  | 'stripePriceId'
  | 'stripePrices'
  | 'embedProvider'
  | 'embedUrl'
>;

// Lignes vides du formulaire écartées. Le back garde aussi le premier prix dans `stripePriceId`.
function normalizeStripePriceOptions(
  stripePrices: StripePriceOptionInput[] | null | undefined,
): StripePriceOptionInput[] {
  return (stripePrices ?? [])
    .map((option) => ({
      priceId: option.priceId.trim(),
      installmentCount: option.installmentCount ?? null,
    }))
    .filter((option) => option.priceId.length > 0);
}

// Le back n'accepte que la donnée du mode choisi : un lien, un code ou des prix Stripe. Pour
// Kalleo et Symetrial, il n'accepte qu'une URL, jamais de code.
export function toOfferConversionInput(
  conversionMode: OfferConversionMode | undefined,
  buttonLink: string | null | undefined,
  embedCode: string | null | undefined,
  stripePrices?: StripePriceOptionInput[] | null,
): OfferConversionInput {
  if (conversionMode === 'EMBED') {
    const urlEmbed = embedCode ? extractUrlEmbed(embedCode) : null;
    if (urlEmbed) {
      return {
        conversionMode: 'EMBED',
        buttonLink: null,
        embedCode: null,
        stripePriceId: null,
        ...urlEmbed,
      };
    }
    const code = embedCode?.trim() || null;
    return {
      conversionMode: 'EMBED',
      buttonLink: null,
      embedCode: code,
      stripePriceId: null,
      // Avec l'indice, un code que le back ne reconnaît pas comme GoHighLevel est refusé au lieu
      // d'être enregistré en iframe générique, que le live n'affiche pas.
      ...(code && isGoHighLevelEmbedCode(parseEmbedCode(code))
        ? { embedProvider: 'GOHIGHLEVEL' as const }
        : {}),
    };
  }
  if (conversionMode === 'STRIPE_EMBEDDED_CHECKOUT') {
    const options = normalizeStripePriceOptions(stripePrices);
    return {
      conversionMode: 'STRIPE_EMBEDDED_CHECKOUT',
      buttonLink: null,
      embedCode: null,
      stripePriceId: options[0]?.priceId ?? null,
      stripePrices: options,
    };
  }
  return {
    conversionMode: 'EXTERNAL_LINK',
    buttonLink: buttonLink?.trim() ?? '',
    embedCode: null,
    stripePriceId: null,
  };
}

function extractUrlEmbed(
  embedCode: string,
): Required<Pick<OfferConversionInput, 'embedProvider' | 'embedUrl'>> | null {
  const kalleoUrl = extractKalleoOfferUrl(embedCode);
  if (kalleoUrl) return { embedProvider: 'KALLEO', embedUrl: kalleoUrl };
  const symetrialUrl = extractSymetrialLiveUrl(embedCode);
  if (symetrialUrl) return { embedProvider: 'SYMETRIAL', embedUrl: symetrialUrl };
  return null;
}

// Ce que le champ « code d'intégration » affiche pour une offre enregistrée : le code collé, ou
// l'URL que le back garde à la place du code pour Kalleo et Symetrial.
export function getOfferEmbedCodeValue(
  content:
    Pick<OfferTemplateContent, 'embedProvider' | 'embedCode' | 'embedUrl'> | null | undefined,
): string {
  if (content?.embedProvider === 'KALLEO' || content?.embedProvider === 'SYMETRIAL') {
    return content.embedUrl ?? '';
  }
  return content?.embedCode ?? '';
}

// L'admin colle l'URL de son offre ou le code fourni par Kalleo (<div data-kalleo>, ou l'iframe
// que crée son script) : on n'en garde que l'URL, le script n'est jamais chargé dans le live.
export function extractKalleoOfferUrl(embedCode: string): string | null {
  const value = embedCode.trim();
  if (!value) return null;
  if (!value.startsWith('<')) return isKalleoOfferUrl(value) ? value : null;
  // DOMParser n'exécute aucun script du code analysé.
  const document = new DOMParser().parseFromString(value, 'text/html');
  const candidate =
    document.querySelector('[data-kalleo]')?.getAttribute('data-kalleo') ??
    document.querySelector('iframe[src]')?.getAttribute('src');
  const url = candidate?.trim();
  return url && isKalleoOfferUrl(url) ? url : null;
}

function isKalleoOfferUrl(value: string): boolean {
  const url = parseHttpsUrl(value);
  return url !== null && KALLEO_HOSTS.has(url.hostname) && url.pathname.length > 1;
}

// L'admin colle le lien donné par Symetrial, souvent avec `?offer=…#e=[Email]&p=[Phone]`, ou son
// iframe. Le back n'accepte que l'URL /live nue : il ajoute lui-même l'offre et le participant.
export function extractSymetrialLiveUrl(embedCode: string): string | null {
  const value = embedCode.trim();
  if (!value) return null;
  // DOMParser n'exécute aucun script du code analysé.
  const candidate = value.startsWith('<')
    ? new DOMParser()
        .parseFromString(value, 'text/html')
        .querySelector('iframe[src]')
        ?.getAttribute('src')
    : value;
  const url = parseHttpsUrl(candidate);
  if (!url || !isSymetrialLiveUrl(url)) return null;
  return `${url.origin}${SYMETRIAL_LIVE_PATH}`;
}

function isSymetrialLiveUrl(url: URL): boolean {
  return isHostOrSubdomain(url.hostname, SYMETRIAL_DOMAIN) && url.pathname === SYMETRIAL_LIVE_PATH;
}

// Date.parse normalise les jours inexistants (31 février → 3 mars) : la date relue doit retomber
// sur les mêmes champs que la chaîne reçue.
function isUtcDateTime(value: string): boolean {
  const match = ISO_UTC_DATE_TIME.exec(value);
  if (!match) return false;
  const [, year, month, day, hours, minutes, seconds = '0'] = match;
  const date = new Date(value);
  return (
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() + 1 === Number(month) &&
    date.getUTCDate() === Number(day) &&
    date.getUTCHours() === Number(hours) &&
    date.getUTCMinutes() === Number(minutes) &&
    date.getUTCSeconds() === Number(seconds)
  );
}

// Envoyé par la page Symetrial une fois le rendez-vous pris, uniquement vers app.welya.io.
export function isSymetrialBookingMessage(
  event: Pick<MessageEvent, 'origin' | 'source' | 'data'>,
  frameWindow: MessageEventSource | null | undefined,
  frameSrc: string,
  offerId: number,
): boolean {
  if (!frameWindow || event.source !== frameWindow) return false;
  if (event.origin !== parseHttpsUrl(frameSrc)?.origin) return false;
  const data: unknown = event.data;
  if (typeof data !== 'object' || data === null) return false;
  const { type, offerId: bookedOfferId, appointmentAt } = data as Record<string, unknown>;
  return (
    type === SYMETRIAL_BOOKED_MESSAGE_TYPE &&
    bookedOfferId === offerId &&
    typeof appointmentAt === 'string' &&
    isUtcDateTime(appointmentAt)
  );
}

// Formules d'une offre enregistrée, telles que le formulaire et la duplication les renvoient.
// Une offre d'avant les formules multiples n'a que `stripePriceId`.
export function getStripePriceOptionInputs(
  content: Pick<OfferTemplateContent, 'stripePriceId' | 'stripePrices'> | null | undefined,
): StripePriceOptionInput[] {
  if (content?.stripePrices?.length) {
    return content.stripePrices.map((option) => ({
      priceId: option.priceId,
      installmentCount: option.installmentCount,
    }));
  }
  return content?.stripePriceId ? [{ priceId: content.stripePriceId, installmentCount: null }] : [];
}

// Recopie d'une offre existante (duplication) : le back réextrait le code et revérifie les prix.
export function copyOfferConversionInput(
  content: Pick<
    OfferTemplateContent,
    | 'conversionMode'
    | 'buttonLink'
    | 'embedCode'
    | 'embedFallbackUrl'
    | 'stripePriceId'
    | 'stripePrices'
    | 'embedProvider'
    | 'embedUrl'
  >,
): OfferConversionInput & Pick<OfferTemplateContentInput, 'embedFallbackUrl'> {
  return {
    ...toOfferConversionInput(
      content.conversionMode,
      content.buttonLink,
      getOfferEmbedCodeValue(content),
      getStripePriceOptionInputs(content),
    ),
    embedFallbackUrl: content.embedFallbackUrl ?? null,
  };
}

const EMBED_CODE_REJECTED_ERRORS = new Set([
  'HTTP.ERROR.400.OFFER.CONTENT.EMBED_CODE.INVALID',
  'HTTP.ERROR.400.OFFER.CONTENT.KALLEO_URL.INVALID',
  'HTTP.ERROR.400.OFFER.CONTENT.KALLEO_URL.REQUIRED',
  'HTTP.ERROR.400.OFFER.CONTENT.KALLEO_URL.TOO_LONG',
  'HTTP.ERROR.400.OFFER.CONTENT.SYMETRIAL_URL.INVALID',
  'HTTP.ERROR.400.OFFER.CONTENT.SYMETRIAL_URL.REQUIRED',
  'HTTP.ERROR.400.OFFER.CONTENT.SYMETRIAL_URL.TOO_LONG',
]);

// Le back renvoie le refus du code (ou de l'URL Kalleo ou Symetrial) dans le détail d'une erreur
// de validation globale.
export function isOfferEmbedCodeRejected(
  error: { message?: string; details?: unknown } | null | undefined,
): boolean {
  if (typeof error?.message === 'string' && EMBED_CODE_REJECTED_ERRORS.has(error.message)) {
    return true;
  }
  if (!Array.isArray(error?.details)) return false;
  return error.details.some((detail: unknown) => {
    const message =
      typeof detail === 'object' && detail !== null
        ? (detail as { message?: unknown }).message
        : undefined;
    return typeof message === 'string' && EMBED_CODE_REJECTED_ERRORS.has(message);
  });
}

// Lien externe historique : un lien saisi sans schéma s'ouvre en https.
export function resolveOfferExternalUrl(buttonLink: string | null | undefined): string | null {
  const link = buttonLink?.trim();
  if (!link) return null;
  return link.startsWith('http://') || link.startsWith('https://') ? link : `https://${link}`;
}

// Le back a déjà extrait et validé ces valeurs ; on revérifie l'hôte avant de construire l'iframe.
export function resolveOfferEmbed(
  content: OfferConversionFields | null | undefined,
  embedDomain: string,
): OfferEmbedRender | null {
  if (!content || !isEmbedOffer(content) || !content.embedProvider) return null;
  const provider = content.embedProvider;

  // Le Pricing Table Stripe envoie l'onglet entier vers Checkout : il ferait quitter le live.
  if (provider === 'STRIPE_PRICING_TABLE') return null;

  const checkoutPlanId = content.checkoutPlanId?.trim() ?? '';
  if (provider === 'WHOP' && WHOP_CHECKOUT_PLAN_ID.test(checkoutPlanId)) {
    return { kind: 'whop-checkout', checkoutPlanId };
  }

  const url = parseHttpsUrl(content.embedUrl);
  if (!url) return null;
  if (provider === 'GOHIGHLEVEL') {
    if (!isGoHighLevelBookingUrl(url)) return null;
    return { kind: 'iframe', provider, src: url.toString() };
  }
  // Un service hors liste (ex. une iframe générique enregistrée avant son retrait) n'est pas affiché.
  if (!PROVIDER_HOSTS[provider]?.(url.hostname)) return null;
  if (provider === 'KALLEO' && url.pathname.length <= 1) return null;
  if (provider === 'SYMETRIAL' && url.pathname !== SYMETRIAL_LIVE_PATH) return null;

  if (provider === 'CALENDLY') {
    // Paramètres que le widget officiel ajoute lui-même pour un affichage intégré.
    url.searchParams.set('embed_domain', embedDomain);
    url.searchParams.set('embed_type', 'Inline');
  }
  return { kind: 'iframe', provider, src: url.toString() };
}

export function resolveOfferEmbedFallbackUrl(
  content: Pick<OfferTemplateContent, 'embedFallbackUrl'> | null | undefined,
): string | null {
  return parseHttpsUrl(content?.embedFallbackUrl)?.toString() ?? null;
}

function parseHttpsUrl(value: string | null | undefined): URL | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  // URL vide le port 443 par défaut : tout port restant est non standard.
  if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
  return url;
}

function isHostOrSubdomain(host: string, domain: string): boolean {
  const normalized = host.toLowerCase();
  return normalized === domain || normalized.endsWith(`.${domain}`);
}

// Iframe que crée le script officiel de Whop, qui ne fonctionne pas isolé du live : on la construit
// nous-mêmes pour ne pas exécuter ce script dans notre page. Whop ne reçoit que l'origine du live,
// jamais son URL, qui porte le jeton du spectateur.
export function buildWhopCheckoutFrameSrc(
  checkoutPlanId: string,
  liveOrigin: string,
  theme: 'light' | 'dark',
): string {
  const url = new URL(`https://whop.com/embedded/checkout/${encodeURIComponent(checkoutPlanId)}/`);
  url.searchParams.set('h', liveOrigin);
  url.searchParams.set('v', '3');
  url.searchParams.set('theme', theme);
  return url.toString();
}

// Le panneau est posé sur le fond de l'identité visuelle, blanc par défaut : sans thème (identité
// absente ou ancienne), on le déduit de ce fond pour garder titre et croix lisibles.
export function isLightOfferPanel(
  visualIdentity: Pick<WorkspaceVisualIdentity, 'theme' | 'backgroundColor'> | null | undefined,
): boolean {
  if (visualIdentity?.theme) return visualIdentity.theme === 'LIGHT';
  return getReadableTextColor(visualIdentity?.backgroundColor ?? '#FFFFFF') === '#000000';
}

// Aperçu du back-office : le panneau du live, alimenté par la variante renvoyée par test-embed.
export function buildOfferEmbedPreview(
  templateId: number,
  content: OfferTemplateContent,
): OfferTemplate {
  return {
    id: templateId,
    webinarId: 0,
    workspaceId: 0,
    privateTitle: '',
    abTesting: false,
    splitPercentA: null,
    contents: [content],
  };
}

// L'embed n'est affiché que pour l'offre encore présentée sur laquelle le spectateur a cliqué.
export function shouldShowOfferEmbed(
  openedOfferEmbedId: number | null,
  offer: Pick<OfferTemplate, 'id' | 'contents'> | null | undefined,
): boolean {
  return (
    openedOfferEmbedId !== null &&
    offer?.id === openedOfferEmbedId &&
    opensInOfferPanel(offer.contents?.[0])
  );
}

// Hors panneau (replay, intervenants) : lien de repli, ou à défaut l'URL validée de l'iframe.
export function resolveOfferEmbedTabUrl(
  content: OfferConversionFields | null | undefined,
): string | null {
  const fallbackUrl = resolveOfferEmbedFallbackUrl(content);
  if (fallbackUrl) return fallbackUrl;
  if (resolveOfferEmbed(content, window.location.host)?.kind !== 'iframe') return null;
  return parseHttpsUrl(content?.embedUrl)?.toString() ?? null;
}

export type OfferEmbedCodeIssue =
  'unsupportedProvider' | 'stripePricingTable' | 'whopOptions' | 'kalleoUrl' | 'symetrialUrl';

// Seuls l'identifiant de plan et le thème (qui suit celui du live) sont repris dans le live.
const SUPPORTED_WHOP_CHECKOUT_ATTRIBUTES = new Set([
  'data-whop-checkout-plan-id',
  'data-whop-checkout-theme',
]);

// Garde-fou du formulaire sur ce que le back accepte mais que le live n'affiche pas fidèlement :
// une iframe vers n'importe quel domaine (GENERIC_IFRAME), le Pricing Table Stripe et les options
// Whop non reprises. Même ordre de détection que l'extracteur back.
// DOMParser n'exécute aucun script du code analysé.
export function getOfferEmbedCodeIssue(embedCode: string): OfferEmbedCodeIssue | null {
  if (extractKalleoOfferUrl(embedCode)) return null;
  // Code qui vise Kalleo sans URL d'offre acceptée (http, sous-domaine, page d'accueil…).
  if (/kalleo\.io/i.test(embedCode)) return 'kalleoUrl';
  if (extractSymetrialLiveUrl(embedCode)) return null;
  // Lien Symetrial hors page /live, en http, sur un faux domaine…
  if (/symetrial\.com/i.test(embedCode)) return 'symetrialUrl';
  const document = parseEmbedCode(embedCode);
  if (document.querySelector('stripe-pricing-table')) return 'stripePricingTable';
  const whopCheckout = document.querySelector('[data-whop-checkout-plan-id]');
  if (whopCheckout) {
    const hasUnsupportedOption = whopCheckout
      .getAttributeNames()
      .some(
        (name) =>
          name.startsWith('data-whop-checkout-') && !SUPPORTED_WHOP_CHECKOUT_ATTRIBUTES.has(name),
      );
    return hasUnsupportedOption ? 'whopOptions' : null;
  }
  if (document.querySelector('[data-tf-widget], [data-tf-live]')) return null;
  for (const attribute of ['data-url', 'data-tf-url']) {
    const value = document.querySelector(`[${attribute}]`)?.getAttribute(attribute);
    if (isKnownProviderUrl(value)) return null;
  }
  if (isGoHighLevelEmbedCode(document)) return null;
  const iframeSrc = document.querySelector('iframe[src]')?.getAttribute('src');
  return parseHttpsUrl(iframeSrc) !== null && !isKnownProviderUrl(iframeSrc)
    ? 'unsupportedProvider'
    : null;
}

// DOMParser n'exécute aucun script du code analysé.
function parseEmbedCode(embedCode: string): Document {
  return new DOMParser().parseFromString(embedCode, 'text/html');
}

// Même règle que le back : le script vient de l'origine de l'iframe, ou c'est le couple du code par
// défaut. Il n'est jamais chargé dans le live : seule l'URL de l'iframe est reprise.
function isGoHighLevelEmbedCode(document: Document): boolean {
  const iframes = document.querySelectorAll('iframe[src]');
  if (iframes.length !== 1) return false;
  const bookingSrc = iframes[0].getAttribute('src') ?? '';
  const bookingUrl = parseHttpsUrl(bookingSrc);
  if (!bookingUrl || !isGoHighLevelBookingUrl(bookingUrl)) return false;
  return Array.from(document.querySelectorAll('script[src]')).some((script) => {
    const scriptSrc = script.getAttribute('src') ?? '';
    const scriptUrl = parseHttpsUrl(scriptSrc);
    if (scriptUrl?.pathname !== GOHIGHLEVEL_EMBED_SCRIPT_PATH) return false;
    const isDefaultPair =
      bookingUrl.hostname === GOHIGHLEVEL_DEFAULT_BOOKING_HOST &&
      scriptUrl.hostname === GOHIGHLEVEL_DEFAULT_EMBED_SCRIPT_HOST;
    return (
      isDefaultPair || writtenOrigin(scriptUrl, scriptSrc) === writtenOrigin(bookingUrl, bookingSrc)
    );
  });
}

function isGoHighLevelBookingUrl(url: URL): boolean {
  return GOHIGHLEVEL_BOOKING_HOSTS.has(url.hostname) && GOHIGHLEVEL_BOOKING_PATH.test(url.pathname);
}

// URL efface le port 443 alors que le back compare le port tel qu'il est écrit.
function writtenOrigin(url: URL, value: string): string {
  const port = EXPLICIT_URL_PORT.exec(value.trim())?.[1];
  return port === undefined ? url.hostname : `${url.hostname}:${Number(port)}`;
}

function isKnownProviderUrl(value: string | null | undefined): boolean {
  const url = parseHttpsUrl(value);
  return (
    url !== null && Object.values(PROVIDER_HOSTS).some((matchesHost) => matchesHost(url.hostname))
  );
}
